/* eslint-disable @typescript-eslint/no-unused-expressions */
import {
  EnvironmentApi,
  environmentApiRef,
} from '@agilelab/plugin-wb-environment';
import { Environment } from '@agilelab/plugin-wb-platform-common';
import { identityApiRef, useApi } from '@mainstage/core-plugin-api';
import {
  createContext,
  Dispatch,
  FC,
  ReactNode,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import useAsyncFn from 'react-use/lib/useAsyncFn';

export interface EnvironmentsContextType {
  environment: Environment;
  environmentList: Environment[];
  environmentAccess: EnvironmentAccessState;
  retryEnvironmentAccess: () => void;
  setEnvironment: (newEnv: Environment) => void;
  disabledStatus: boolean;
  setDisabledStatus: Dispatch<SetStateAction<boolean>>;
  priorityEnvironment: Environment;
}

export type EnvironmentAccessState =
  | { status: 'loading'; environments: []; error?: undefined }
  | {
      status: 'empty' | 'ready';
      environments: Environment[];
      error?: undefined;
    }
  | {
      status: 'authentication-error' | 'retryable-error';
      environments: [];
      error: Error;
    };

const noEnv: Environment = {
  name: '',
  priority: 0,
};

const environmentStorageKey = 'environment';

const loadStoredEnvironment = (): Environment | undefined => {
  try {
    const serializedEnvironment = window.localStorage.getItem(
      environmentStorageKey,
    );
    return serializedEnvironment
      ? (JSON.parse(serializedEnvironment) as Environment)
      : undefined;
  } catch {
    return undefined;
  }
};

export const EnvironmentsContext = createContext<EnvironmentsContextType>(
  {} as EnvironmentsContextType,
);

export interface EnvironmentsContextProviderProps {
  children?: ReactNode;
}

export const EnvironmentsContextProvider: FC<
  EnvironmentsContextProviderProps
> = ({ children }) => {
  const environmentApi: EnvironmentApi = useApi(environmentApiRef);
  const identityApi = useApi(identityApiRef);
  const [disabledStatus, setDisabledStatus] = useState<boolean>(false);
  const [preferredEnvironment, setPreferredEnvironment] = useState<Environment>(
    () => loadStoredEnvironment() ?? noEnv,
  );

  const [fetchEnvironmentsState, fetchEnvironments] = useAsyncFn(async () => {
    const credentials = await identityApi.getCredentials();
    return environmentApi.getAccessibleEnvironments(credentials);
  }, [identityApi, environmentApi]);

  useEffect(() => {
    fetchEnvironments();
  }, [fetchEnvironments]);

  const environmentAccess = useMemo<EnvironmentAccessState>(() => {
    if (fetchEnvironmentsState.error) {
      const status = (
        fetchEnvironmentsState.error as Error & { status?: number }
      ).status;
      return {
        status:
          status === 401 || status === 403
            ? 'authentication-error'
            : 'retryable-error',
        environments: [],
        error: fetchEnvironmentsState.error,
      };
    }
    if (fetchEnvironmentsState.loading || !fetchEnvironmentsState.value) {
      return { status: 'loading', environments: [] };
    }
    return {
      status: fetchEnvironmentsState.value.length ? 'ready' : 'empty',
      environments: fetchEnvironmentsState.value,
    };
  }, [fetchEnvironmentsState]);

  const allowedEnvironments = environmentAccess.environments;
  const priorityEnvironment =
    allowedEnvironments.reduce<Environment | undefined>(
      (highestPriority, env) =>
        !highestPriority || env.priority < highestPriority.priority
          ? env
          : highestPriority,
      undefined,
    ) ?? noEnv;
  const environment =
    allowedEnvironments.find(env => env.name === preferredEnvironment.name) ??
    priorityEnvironment;

  const setEnvironment = useCallback(
    (newEnv: Environment) => {
      const accessibleEnvironment = allowedEnvironments.find(
        env => env.name === newEnv.name,
      );
      if (!accessibleEnvironment) return;
      setPreferredEnvironment(accessibleEnvironment);
    },
    [allowedEnvironments],
  );

  useEffect(() => {
    if (environmentAccess.status === 'ready') {
      window.localStorage.setItem(
        environmentStorageKey,
        JSON.stringify(environment),
      );
    }
  }, [environment, environmentAccess.status]);

  const contextValue = useMemo(
    () => ({
      environment,
      environmentList: allowedEnvironments,
      environmentAccess,
      retryEnvironmentAccess: fetchEnvironments,
      setEnvironment,
      disabledStatus,
      setDisabledStatus,
      priorityEnvironment,
    }),
    [
      environment,
      allowedEnvironments,
      environmentAccess,
      setEnvironment,
      disabledStatus,
      setDisabledStatus,
      priorityEnvironment,
      fetchEnvironments,
    ],
  );

  return (
    <EnvironmentsContext.Provider value={contextValue}>
      {children}
    </EnvironmentsContext.Provider>
  );
};

export const useEnvironmentsContext = () => useContext(EnvironmentsContext);
